import { useState } from "react";

import { Eye, FolderCog } from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { CenterInfraHeader } from "@/features/servers/components/CenterInfraHeader";
import { HostsSection } from "@/features/servers/components/HostsSection";
import { SoftwareSection } from "@/features/servers/components/SoftwareSection";
import { SoftwareTypesDialog } from "@/features/servers/components/SoftwareTypesDialog";
import type { User } from "@/features/users/schemas/user";
import { getUserFullName } from "@/features/users/utils/user-utils";

interface CenterInfraDetailProps {
  institutionUid: string;
  user: User | null | undefined;
  /** Si true, muestra botón de catálogo (solo admin). */
  canManageCatalog?: boolean;
}

/**
 * Vista de edición de la ficha de infraestructura.
 */
export function CenterInfraDetail({
  institutionUid,
  user,
  canManageCatalog = false,
}: CenterInfraDetailProps) {
  const [catalogOpen, setCatalogOpen] = useState(false);
  const centerName = user ? getUserFullName(user) : institutionUid;

  return (
    <div className="space-y-5">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-1">
          <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Edición de infraestructura
          </p>
          <h1 className="text-2xl font-semibold tracking-tight truncate">
            {centerName}
          </h1>
          <p className="text-sm text-muted-foreground">
            {user?.email ? `${user.email} · ` : ""}
            La dirección de ficha no modifica el perfil del centro
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canManageCatalog && (
            <Button
              variant="outline"
              size="sm"
              className="cursor-pointer"
              onClick={() => setCatalogOpen(true)}
            >
              <FolderCog className="h-4 w-4 mr-1.5" aria-hidden />
              Catálogo software
            </Button>
          )}
          <Button variant="outline" size="sm" className="cursor-pointer" asChild>
            <Link to={`/servers/${institutionUid}`}>
              <Eye className="h-4 w-4 mr-1.5" aria-hidden />
              Ver ficha
            </Link>
          </Button>
        </div>
      </header>

      <div className="space-y-4">
        <CenterInfraHeader institutionUid={institutionUid} user={user} />
        <HostsSection institutionUid={institutionUid} />
        <SoftwareSection institutionUid={institutionUid} />
      </div>

      <SoftwareTypesDialog
        open={catalogOpen}
        onClose={() => setCatalogOpen(false)}
      />
    </div>
  );
}
